import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/navigation/Header';
import Select from '../../components/ui/Select';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';

const AOIReports = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [generatingReport, setGeneratingReport] = useState(false);

  const [filters, setFilters] = useState({
    search: '',
    projectType: 'all',
    aoiType: 'all',
    reportType: 'satellite',
    dateFrom: '',
    dateTo: ''
  });

  const [selectedAOIs, setSelectedAOIs] = useState([]);

  const mockAOIs = [
    {
      id: 'AOI-001',
      name: 'Zona Norte Amazonía',
      projectId: 'PRJ-2026-001',
      projectName: 'Restauración Forestal Amazonía',
      type: 'grouped',
      area: 2500,
      country: 'Colombia',
      lastAnalysis: '2026-01-15'
    },
    {
      id: 'AOI-002',
      name: 'Sector Ucayali Central',
      projectId: 'PRJ-2026-002',
      projectName: 'Conservación Bosque Nublado',
      type: 'grouped',
      area: 1800,
      country: 'Perú',
      lastAnalysis: '2026-01-18'
    },
    {
      id: 'AOI-003',
      name: 'Área Piloto Chocó',
      projectId: null,
      projectName: 'Independiente',
      type: 'independent',
      area: 450,
      country: 'Colombia',
      lastAnalysis: '2026-01-20'
    },
    {
      id: 'AOI-004',
      name: 'Zona Experimental Pampa',
      projectId: null,
      projectName: 'Independiente',
      type: 'independent',
      area: 320,
      country: 'Argentina',
      lastAnalysis: '2026-01-19'
    },
    {
      id: 'AOI-005',
      name: 'Corredor Biológico Andes',
      projectId: 'PRJ-2026-006',
      projectName: 'Reforestación Andes Ecuatorianos',
      type: 'grouped',
      area: 1950,
      country: 'Ecuador',
      lastAnalysis: '2026-01-17'
    },
    {
      id: 'AOI-006',
      name: 'Reserva Bosque Seco',
      projectId: 'PRJ-2026-007',
      projectName: 'Protección Bosque Seco Tropical',
      type: 'grouped',
      area: 3200,
      country: 'Bolivia',
      lastAnalysis: '2026-01-16'
    }
  ];

  const filteredAOIs = useMemo(() => {
    return mockAOIs?.filter((aoi) => {
      const matchesSearch =
        filters?.search === '' ||
        aoi?.name?.toLowerCase()?.includes(filters?.search?.toLowerCase()) ||
        aoi?.id?.toLowerCase()?.includes(filters?.search?.toLowerCase()) ||
        aoi?.projectName?.toLowerCase()?.includes(filters?.search?.toLowerCase());

      const matchesAOIType =
        filters?.aoiType === 'all' || aoi?.type === filters?.aoiType;

      const matchesProjectType =
        filters?.projectType === 'all' ||
        (filters?.projectType === 'independent' && !aoi?.projectId) ||
        (filters?.projectType === 'grouped' && aoi?.projectId);

      return matchesSearch && matchesAOIType && matchesProjectType;
    });
  }, [filters, mockAOIs]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleResetFilters = () => {
    setFilters({
      search: '',
      projectType: 'all',
      aoiType: 'all',
      reportType: 'satellite',
      dateFrom: '',
      dateTo: ''
    });
    setSelectedAOIs([]);
  };

  const handleSelectAOI = (aoiId) => {
    setSelectedAOIs((prev) => {
      if (prev?.includes(aoiId)) {
        return prev?.filter((id) => id !== aoiId);
      }
      return [...prev, aoiId];
    });
  };

  const handleSelectAll = () => {
    if (selectedAOIs?.length === filteredAOIs?.length) {
      setSelectedAOIs([]);
    } else {
      setSelectedAOIs(filteredAOIs?.map((aoi) => aoi?.id));
    }
  };

  const handleGenerateReport = async () => {
    if (selectedAOIs?.length === 0) {
      alert('Por favor seleccione al menos un AOI');
      return;
    }

    setGeneratingReport(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, 2000));
      console.log('Generating report for AOIs:', selectedAOIs);
      console.log('Report type:', filters?.reportType);
      console.log('Date range:', filters?.dateFrom, 'to', filters?.dateTo);
      alert('Reporte generado exitosamente');
    } catch (error) {
      console.error('Error generating report:', error);
    } finally {
      setGeneratingReport(false);
    }
  };

  const aoiTypeOptions = [
    { value: 'all', label: 'Todos los tipos de AOI' },
    { value: 'independent', label: 'AOI Independientes' },
    { value: 'grouped', label: 'AOI de Proyectos Agrupados' }
  ];

  const projectTypeOptions = [
    { value: 'all', label: 'Todos los proyectos' },
    { value: 'independent', label: 'Solo Independientes' },
    { value: 'grouped', label: 'Solo Agrupados' }
  ];

  const reportTypeOptions = [
    { value: 'satellite', label: 'Análisis de Imágenes Satelitales' },
    { value: 'gee', label: 'Reportes GEE (Google Earth Engine)' },
    { value: 'combined', label: 'Reporte Combinado (Satelital + GEE)' }
  ];

  return (
    <>
      <Header />
      <div className="min-h-screen bg-background pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-2 text-sm text-muted-foreground caption">
              <span
                className="cursor-pointer hover:text-foreground transition-colors"
                onClick={() => navigate('/main-dashboard')}
              >
                Dashboard
              </span>
              <Icon name="ChevronRight" size={14} />
              <span className="text-foreground">Reportes AOI</span>
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-foreground font-mono mb-2">
              Generación de Reportes AOI
            </h1>
            <p className="text-muted-foreground">
              Genere reportes de análisis satelital y GEE para Áreas de Interés (AOI) independientes o agrupadas
            </p>
          </div>

          <div className="bg-card border border-border rounded-lg p-6 mb-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg">
                <Icon name="Filter" size={20} color="var(--color-primary)" />
              </div>
              <h2 className="text-xl font-semibold font-mono">Filtros de Búsqueda</h2>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  type="search"
                  label="Buscar AOI"
                  placeholder="Nombre, ID o proyecto..."
                  value={filters?.search}
                  onChange={(e) => handleFilterChange('search', e?.target?.value)}
                />
                <Select
                  label="Tipo de AOI"
                  options={aoiTypeOptions}
                  value={filters?.aoiType}
                  onChange={(value) => handleFilterChange('aoiType', value)}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Select
                  label="Asociación a Proyecto"
                  options={projectTypeOptions}
                  value={filters?.projectType}
                  onChange={(value) => handleFilterChange('projectType', value)}
                />
                <Select
                  label="Tipo de Reporte"
                  options={reportTypeOptions}
                  value={filters?.reportType}
                  onChange={(value) => handleFilterChange('reportType', value)}
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  type="date"
                  label="Fecha Desde"
                  value={filters?.dateFrom}
                  onChange={(e) => handleFilterChange('dateFrom', e?.target?.value)}
                />
                <Input
                  type="date"
                  label="Fecha Hasta"
                  value={filters?.dateTo}
                  onChange={(e) => handleFilterChange('dateTo', e?.target?.value)}
                />
              </div>

              <div className="flex items-center gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={handleResetFilters}
                  iconName="RotateCcw"
                  iconPosition="left"
                >
                  Restablecer Filtros
                </Button>
              </div>
            </div>
          </div>

          <div className="bg-card border border-border rounded-lg p-6 mb-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="flex items-center justify-center w-10 h-10 bg-accent/10 rounded-lg">
                  <Icon name="MapPin" size={20} color="var(--color-accent)" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold font-mono">Áreas de Interés (AOI)</h2>
                  <p className="text-sm text-muted-foreground caption">
                    {filteredAOIs?.length} AOI{filteredAOIs?.length !== 1 ? 's' : ''} encontrada{filteredAOIs?.length !== 1 ? 's' : ''} • {selectedAOIs?.length} seleccionada{selectedAOIs?.length !== 1 ? 's' : ''}
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleSelectAll}
                iconName={selectedAOIs?.length === filteredAOIs?.length ? 'CheckSquare' : 'Square'}
                iconPosition="left"
              >
                {selectedAOIs?.length === filteredAOIs?.length ? 'Deseleccionar' : 'Seleccionar'} Todo
              </Button>
            </div>

            {filteredAOIs?.length === 0 ? (
              <div className="text-center py-12">
                <Icon name="Search" size={48} className="mx-auto mb-4 text-muted-foreground" />
                <p className="text-muted-foreground">No se encontraron AOI con los filtros seleccionados</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredAOIs?.map((aoi) => (
                  <div
                    key={aoi?.id}
                    onClick={() => handleSelectAOI(aoi?.id)}
                    className={`
                      border rounded-lg p-4 cursor-pointer transition-all duration-250
                      ${selectedAOIs?.includes(aoi?.id)
                        ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50 hover:bg-muted/50'
                      }
                    `}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div
                          className={`
                            flex items-center justify-center w-5 h-5 rounded border-2 transition-colors
                            ${selectedAOIs?.includes(aoi?.id)
                              ? 'border-primary bg-primary' :'border-muted-foreground'
                            }
                          `}
                        >
                          {selectedAOIs?.includes(aoi?.id) && (
                            <Icon name="Check" size={14} color="white" />
                          )}
                        </div>
                        <span
                          className={`
                            px-2 py-1 text-xs rounded caption
                            ${aoi?.type === 'independent' ?'bg-accent/10 text-accent' :'bg-primary/10 text-primary'
                            }
                          `}
                        >
                          {aoi?.type === 'independent' ? 'Independiente' : 'Agrupado'}
                        </span>
                      </div>
                    </div>

                    <h3 className="font-semibold text-foreground mb-2">{aoi?.name}</h3>
                    <p className="text-sm text-muted-foreground caption mb-3">
                      ID: {aoi?.id}
                    </p>

                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Icon name="FolderTree" size={14} />
                        <span className="caption">{aoi?.projectName}</span>
                      </div>
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Icon name="MapPin" size={14} />
                        <span className="caption">{aoi?.country}</span>
                      </div>
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Icon name="Maximize2" size={14} />
                        <span className="caption">{aoi?.area?.toLocaleString()} ha</span>
                      </div>
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Icon name="Calendar" size={14} />
                        <span className="caption">
                          Último análisis: {new Date(aoi?.lastAnalysis)?.toLocaleDateString('es-ES')}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {selectedAOIs?.length > 0 && (
            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex items-center justify-center w-10 h-10 bg-success/10 rounded-lg">
                    <Icon name="FileText" size={20} color="var(--color-success)" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground">Generar Reporte</h3>
                    <p className="text-sm text-muted-foreground caption">
                      {selectedAOIs?.length} AOI seleccionada{selectedAOIs?.length !== 1 ? 's' : ''} • Tipo: {reportTypeOptions?.find(opt => opt?.value === filters?.reportType)?.label}
                    </p>
                  </div>
                </div>
                <Button
                  variant="default"
                  onClick={handleGenerateReport}
                  loading={generatingReport}
                  iconName="Download"
                  iconPosition="left"
                >
                  Generar Reporte
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default AOIReports;